import { BackendFetch } from "@hantera/portal-app";
import { BackendGraph, parseGraphJson } from "./graph";

export interface SkuIncomingStock {
  incomingStockId: string;
  reference?: string;
  inventoryKey?: string;
  expectedOn?: unknown;
  quantity: unknown;
}

export interface SkuAllocation {
  stockAllocationId: string;
  reference?: string;
  inventoryKey?: string;
  quantity: unknown;
  from?: unknown;
  until?: unknown;
}

export interface SkuOrderRef {
  orderId?: string;
  orderNumber?: string;
}

export interface SkuReservationAllocationRef {
  stockAllocationId?: string;
  reference?: string;
}

export interface SkuReservationIncomingRef {
  incomingStockId?: string;
  reference?: string;
  expectedOn?: unknown;
}

export interface SkuReservation {
  stockReservationId: string;
  inventoryKey?: string;
  quantity: unknown;
  inventoryDate?: unknown;
  priority?: unknown;
  order?: SkuOrderRef;
  allocation?: SkuReservationAllocationRef;
  incomingStock?: SkuReservationIncomingRef;
}

export interface SkuBackOrderRequestRef {
  stockReservationRequestId?: string;
  stockAllocations?: string[];
}

export interface SkuBackOrder {
  backOrderId: string;
  inventoryKey?: string;
  quantity: unknown;
  inventoryDate?: unknown;
  priority?: unknown;
  order?: SkuOrderRef;
  reservationRequest?: SkuBackOrderRequestRef;
}

export interface SkuStockPosition {
  stockPositionId: string;
  inventoryKey?: string;
  physicalStock: unknown;
  incomingStock?: SkuIncomingStock[];
  allocations?: SkuAllocation[];
  reservations?: SkuReservation[];
  backOrders?: SkuBackOrder[];
}


export interface Sku {
  skuId: string;
  skuNumber?: string;
  createdAt?: unknown;
  dynamic?: Record<string, unknown>;
  stockPositions?: SkuStockPosition[];
}

export interface AvailableIncomingStock {
  incomingStockId: string;
  expectedOn: unknown;
  availableQuantity: unknown;
  availableTotalQuantity: unknown;
}

export interface AvailableStock {
  physicalQuantity: unknown;
  incomingStock: AvailableIncomingStock[];
}

export interface AvailableStockResult {
  unallocated: AvailableStock;
  byAllocation: Record<string, AvailableStock>;
}

export interface PhysicalStockPoint {
  timestamp: unknown;
  quantity: unknown;
  identityId: string;
}

export interface IncomingStockEvent {
  timestamp: unknown;
  incomingStockId: string;
  reference?: string;
  expectedOn?: unknown;
  quantity?: unknown;
  deleted: boolean;
}

export interface AllocationEvent {
  timestamp: unknown;
  stockAllocationId: string;
  reference?: string;
  quantity?: unknown;
  from?: unknown;
  until?: unknown;
  deleted: boolean;
}

export interface ReservationEvent {
  timestamp: unknown;
  stockReservationId: string;
  orderId?: string;
  quantity?: unknown;
  inventoryDate?: unknown;
  deleted: boolean;
}

export interface BackOrderEvent {
  timestamp: unknown;
  backOrderId: string;
  orderId?: string;
  quantity?: unknown;
  inventoryDate?: unknown;
  deleted: boolean;
}

export interface InventoryStockHistory {
  physicalStock: PhysicalStockPoint[];
  allocations: AllocationEvent[];
  reservations: ReservationEvent[];
  backOrders: BackOrderEvent[];
}


export interface StockHistoryResult {
  truncatedBefore?: unknown;
  inventories: Record<string, InventoryStockHistory>;
}

export default function (portalFetch: BackendFetch, graph: BackendGraph) {
  return {
    /**
     * Loads the current SKU snapshot from the graph, including stock positions, incoming stock and allocations.
     */
    async get(selector: { type: "skuId" | "skuNumber"; value: string }): Promise<Sku | undefined> {
      const result = await graph.queryGraph<Sku>({
        edge: "skus",
        limit: 1,
        filter: `${selector.type} == ${selector.type === "skuId" ? selector.value : `'${selector.value}'`}`,
        node: {
          fields: ["skuId", "skuNumber", "createdAt", "dynamic"],
          navigate: [
            {
              edge: "stockPositions",
              node: {
                fields: ["stockPositionId", "inventoryKey", "physicalStock"],
                navigate: [
                  {
                    edge: "incomingStock",
                    node: {
                      fields: ["incomingStockId", "reference", "inventoryKey", "expectedOn", "quantity"],
                    },
                  },
                  {
                    edge: "allocations",
                    node: {
                      fields: ["stockAllocationId", "reference", "inventoryKey", "quantity", "from", "until"],
                    },
                  },
                  {
                    edge: "reservations",
                    node: {
                      fields: ["stockReservationId", "quantity", "inventoryDate", "priority"],
                      navigate: [
                        {
                          edge: "order",
                          node: {
                            fields: ["orderId", "orderNumber"],
                          },
                        },
                        {
                          edge: "allocation",
                          node: {
                            fields: ["stockAllocationId", "reference"],
                          },
                        },
                        {
                          edge: "incomingStock",
                          node: {
                            fields: ["incomingStockId", "reference", "expectedOn"],
                          },
                        },
                      ],
                    },
                  },
                  {
                    edge: "backOrders",
                    node: {
                      fields: ["backOrderId", "quantity", "inventoryDate", "priority"],
                      navigate: [
                        {
                          edge: "order",
                          node: {
                            fields: ["orderId", "orderNumber"],
                          },
                        },
                        {
                          edge: "reservationRequest",
                          node: {
                            fields: ["stockReservationRequestId", "stockAllocations"],
                          },
                        },
                      ],
                    },
                  },
                ],

              },
            },
          ],
        },
      });

      return result[0];
    },

    /**
     * Calculates available-to-promise stock per inventory via the SkuActor.
     */
    async getAvailableStock(skuId: string): Promise<Record<string, AvailableStockResult>> {
      return sendMessage<Record<string, AvailableStockResult>>(skuId, "calculateAvailableStock", {});
    },

    /**
     * Loads the stock change history (physical stock, incoming and allocations) from the SkuActor mutation log.
     */
    async getStockHistory(skuId: string): Promise<StockHistoryResult> {
      return sendMessage<StockHistoryResult>(skuId, "getStockHistory", {});
    },
  };

  async function sendMessage<T>(skuId: string, type: string, body: Record<string, unknown>): Promise<T> {
    const response = await portalFetch(`/core/resources/actors/sku/${skuId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify([{ type, responseKey: type, body }]),
    });

    if (!response.ok) {
      throw await response.errors();
    }

    const result = parseGraphJson<{ data: Record<string, T> }>(await response.text());
    return result.data[type];
  }
}
